import { useCallback, useEffect, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { adsApi } from '@/api/client';
import {
  parseWorkspaceSearchTermNegateJob,
  parseWorkspaceSearchTermNegateResult,
  WORKSPACE_SEARCH_TERM_NEGATE_TERMINAL_STATUSES,
  type WorkspaceSearchTermNegateJob,
  type WorkspaceSearchTermNegateJobCreateBody,
  type WorkspaceSearchTermNegateResult,
} from '@/api/workspaceSearchTermNegateTypes';

const POLL_MS = 2000;

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

async function pollJobUntilDone(
  profileId: string,
  jobId: string,
  onUpdate: (job: WorkspaceSearchTermNegateJob) => void,
  abortRef: { current: boolean }
): Promise<WorkspaceSearchTermNegateJob> {
  for (;;) {
    const res = await adsApi.getWorkspaceSearchTermNegateJob(profileId, jobId);
    const job = parseWorkspaceSearchTermNegateJob(res.data);
    if (!job) throw new Error('Invalid negate job response');
    onUpdate(job);
    if (WORKSPACE_SEARCH_TERM_NEGATE_TERMINAL_STATUSES.has(job.status)) {
      return job;
    }
    if (abortRef.current) return job;
    await sleep(POLL_MS);
  }
}

export function useWorkspaceSearchTermNegateJob(profileId: string) {
  const queryClient = useQueryClient();
  const [job, setJob] = useState<WorkspaceSearchTermNegateJob | null>(null);
  const [failedResults, setFailedResults] = useState<WorkspaceSearchTermNegateResult[]>([]);
  const abortRef = useRef(false);

  useEffect(() => {
    abortRef.current = false;
    return () => {
      abortRef.current = true;
    };
  }, [profileId]);

  useEffect(() => {
    if (!profileId) return;
    let cancelled = false;
    setJob(null);
    setFailedResults([]);
    void (async () => {
      try {
        const res = await adsApi.getLatestWorkspaceSearchTermNegateJob(profileId);
        const latest = parseWorkspaceSearchTermNegateJob(res.data);
        if (cancelled || !latest || (latest.totals.failed ?? 0) <= 0) return;
        let adopted = false;
        setJob((current) => {
          if (current) return current;
          adopted = true;
          return latest;
        });
        if (adopted) {
          const failedRes = await adsApi.listWorkspaceSearchTermNegateJobResults(profileId, latest.id, {
            status: 'failed',
            limit: 100,
            offset: 0,
          });
          if (cancelled) return;
          const raw = (failedRes.data as { results?: unknown[] } | null)?.results ?? [];
          setFailedResults(
            raw
              .map((item) => parseWorkspaceSearchTermNegateResult(item))
              .filter((item): item is WorkspaceSearchTermNegateResult => item != null)
          );
        }
      } catch {
        // The search-term table still works if the last job cannot be loaded.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [profileId]);

  const invalidate = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ['workspace-search-term-page', profileId] });
  }, [profileId, queryClient]);

  const loadFailedResults = useCallback(
    async (jobId: string) => {
      const res = await adsApi.listWorkspaceSearchTermNegateJobResults(profileId, jobId, {
        status: 'failed',
        limit: 100,
        offset: 0,
      });
      const raw = (res.data as { results?: unknown[] } | null)?.results ?? [];
      setFailedResults(
        raw
          .map((item) => parseWorkspaceSearchTermNegateResult(item))
          .filter((item): item is WorkspaceSearchTermNegateResult => item != null)
      );
    },
    [profileId]
  );

  const startMutation = useMutation({
    mutationFn: async (body: WorkspaceSearchTermNegateJobCreateBody) => {
      const createRes = await adsApi.createWorkspaceSearchTermNegateJob(profileId, body);
      const created = parseWorkspaceSearchTermNegateJob(createRes.data);
      if (!created) throw new Error('Failed to start negate job');
      setJob(created);
      setFailedResults([]);
      if (WORKSPACE_SEARCH_TERM_NEGATE_TERMINAL_STATUSES.has(created.status)) {
        if ((created.totals.failed ?? 0) > 0) {
          await loadFailedResults(created.id);
        }
        return created;
      }
      const final = await pollJobUntilDone(profileId, created.id, setJob, abortRef);
      if ((final.totals.failed ?? 0) > 0) {
        await loadFailedResults(final.id);
      }
      return final;
    },
    onSettled: () => {
      invalidate();
    },
  });

  const retryMutation = useMutation({
    mutationFn: async (jobId: string) => {
      const retryRes = await adsApi.retryWorkspaceSearchTermNegateJob(profileId, jobId);
      const restarted = parseWorkspaceSearchTermNegateJob(retryRes.data);
      if (!restarted) throw new Error('Failed to retry negate job');
      setJob(restarted);
      setFailedResults([]);
      const final = await pollJobUntilDone(profileId, restarted.id, setJob, abortRef);
      if ((final.totals.failed ?? 0) > 0) {
        await loadFailedResults(final.id);
      }
      return final;
    },
    onSettled: () => {
      invalidate();
    },
  });

  const isRunning =
    startMutation.isPending ||
    retryMutation.isPending ||
    (job != null && !WORKSPACE_SEARCH_TERM_NEGATE_TERMINAL_STATUSES.has(job.status));

  return {
    job,
    failedResults,
    isRunning,
    start: startMutation.mutateAsync,
    retryFailed: retryMutation.mutateAsync,
    reset: () => {
      setJob(null);
      setFailedResults([]);
      startMutation.reset();
      retryMutation.reset();
    },
    error: startMutation.error ?? retryMutation.error,
  };
}
